import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout as logoutAction } from './authSlice';
import type { AuthState } from './types';

type RootWithAuth = { auth: AuthState };

/**
 * Validates the persisted session on app startup AND periodically
 * thereafter against the live API.
 *
 * Flow:
 * 1. Reads `auth.session` from the rehydrated Redux store. Bails if absent.
 * 2. **Initial check** — calls `GET /auth/session/{sessionId}` for the
 *    metadata view (isRevoked, expiry, ip/userAgent).
 * 3. **Drift check (periodic, every 2 min)** — calls `GET /api/Auth/me`.
 *    The server returns whichever sessionId the JWT was issued against.
 *    If that no longer matches the sessionId persisted in this tab the
 *    user has signed in elsewhere and the backend rotated the active
 *    session → force a logout so this tab can't keep using a stale
 *    refresh token. Also force-logout if the server reports
 *    `isAuthenticated: false`.
 * 4. If any check fails, dispatches `logout('refresh-failed')` so the
 *    SessionEndedBanner surfaces and `RequireAuth` redirects to `/login`.
 *
 * The drift check honours the user requirement: "if session is changed
 * it should be log out". Combined with the storage swap to
 * `sessionStorage` (closing the tab/browser clears the token) this
 * gives bank-grade session hygiene.
 *
 * Mount this once near the root of the app (the app's `<App />`
 * component) after `<Providers />`.
 */
const SESSION_DRIFT_POLL_MS = 2 * 60 * 1000; // 2 minutes

export function useSessionBootstrap() {
  const dispatch = useDispatch();
  const session = useSelector((s: RootWithAuth) => s.auth.session);
  const isAuthenticated = useSelector((s: RootWithAuth) => s.auth.isAuthenticated);
  const ran = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !session) {
      ran.current = false;
      return;
    }

    const sessionId = session?.sessionId;
    if (!sessionId) return;

    // Local expiry pre-check before hitting the API. Without this, a
    // user who opens the app a day after closing it would see an
    // authenticated UI for a moment, then a 401 cascade.
    //
    // Note: with auth persisted to **sessionStorage** (per the v3
    // bump in apps/web/src/store.ts) this branch will almost never
    // fire — closing the tab nukes the token. Kept as a belt for the
    // edge case where a long-lived tab outlives the refresh window.
    const expiresAt = session.expiresAt ? Date.parse(session.expiresAt) : NaN;
    if (Number.isFinite(expiresAt)) {
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - expiresAt > sevenDaysMs) {
        dispatch(logoutAction('expired'));
        return;
      }
    }

    let cancelled = false;

    const checkServerSession = async (initial: boolean) => {
      try {
        const api = await import('@kss/api');

        // Drift check — does the JWT the server sees still point at
        // OUR sessionId? If the user signed in from another device
        // the backend rotates the active session and any /me call
        // returns the new id.
        try {
          const me = await api.authService.me();
          if (cancelled) return;
          if (me?.isAuthenticated === false) {
            dispatch(logoutAction('refresh-failed'));
            return;
          }
          if (me?.sessionId && me.sessionId !== sessionId) {
            dispatch(logoutAction('refresh-failed'));
            return;
          }
        } catch (meErr) {
          // 404 / 405 → endpoint not mounted on this deploy, skip.
          // 401 → interceptor will already have tried refresh; if it
          // bubbled up the refresh failed, so log out.
          const status =
            (meErr as { response?: { status?: number } } | undefined)?.response
              ?.status;
          if (status === 401) {
            dispatch(logoutAction('refresh-failed'));
            return;
          }
        }

        if (!initial) return;

        // Initial bootstrap also pings the session metadata endpoint
        // for an explicit isRevoked signal (server-side admin revoke).
        try {
          const meta = await api.authService.session(sessionId);
          if (cancelled) return;
          if (meta?.active === false || meta?.info?.isRevoked) {
            dispatch(logoutAction('refresh-failed'));
          }
        } catch (err) {
          const status =
            (err as { response?: { status?: number } } | undefined)?.response
              ?.status;
          if (status === 401) {
            dispatch(logoutAction('refresh-failed'));
          }
          // 404/405/network → leave the JWT alone; the periodic /me
          // poll is the authoritative source of truth.
        }
      } catch {
        // dynamic import of @kss/api failed — nothing we can do.
      }
    };

    if (!ran.current) {
      ran.current = true;
      void checkServerSession(true);
    }

    const timer = setInterval(() => {
      void checkServerSession(false);
    }, SESSION_DRIFT_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [dispatch, isAuthenticated, session]);
}
